// Updated IVRBlastTable.js - Checkbox UI Fix
import React, { useEffect, useState, useRef } from "react";
import Icon from "@mui/material/Icon";
import Loader from "../../../Loader/loader";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  TablePagination,
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import {
  getIVRBlastListRequest,
  changeCampaignStatusRequest,
  deleteCampaignRequest,
  getStartRequest,
  getStopRequest,
} from "../../../api/api";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import IVRBlastForm from "./IVRBlastForm";
import PanToolIcon from "@mui/icons-material/PanTool";
import AddBoxIcon from "@mui/icons-material/AddBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DeleteForm from "../../DeleteAlert/DeleteForm";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// AudioToggle component
const AudioToggle = ({ audioUrls }) => {
  const [currentAudio, setCurrentAudio] = useState(null);
  const audioRef = useRef(null);

  const handleToggle = (url) => {
    if (audioRef.current && currentAudio === url) {
      audioRef.current.pause();
      setCurrentAudio(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.crossOrigin = "anonymous";
      audio.play().catch((err) => console.error("Playback failed:", err));
      audioRef.current = audio;
      setCurrentAudio(url);
    }
  };

  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      {audioUrls.filter(Boolean).map((url, idx) => (
        <Tooltip key={idx} title={`Audio ${idx + 1}`}>
          <Icon
            fontSize="small"
            sx={{ cursor: "pointer", color: currentAudio === url ? "red" : "#1A73E8" }}
            onClick={() => handleToggle(url)}
          >
            {currentAudio === url ? "pause_circle" : "play_circle"}
          </Icon>
        </Tooltip>
      ))}
    </MDBox>
  );
};

AudioToggle.propTypes = {
  audioUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function IVRBlastTable() {
  const [rows, setRows] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Multi-select states
  const [selectedRows, setSelectedRows] = useState([]);

  // Multi-select handlers
  const handleSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedRows.length === campaignData.length && campaignData.length > 0) {
      setSelectedRows([]);
    } else {
      const allIds = campaignData.map((item) => item.id);
      setSelectedRows([...allIds]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prevSelected) => {
      const stringId = String(id);
      const isAlreadySelected = prevSelected.some((selectedId) => String(selectedId) === stringId);

      if (isAlreadySelected) {
        return prevSelected.filter((selectedId) => String(selectedId) !== stringId);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  useEffect(() => {
    if (campaignData.length > 0) {
      setRows(generateRows(campaignData));
    }
  }, [selectedRows, campaignData]);

  const handleStart = async (id) => {
    try {
      await getStartRequest({ id });
      fetchData();
    } catch (error) {
      console.error("Start API Error:", error);
    }
  };

  const handleStop = async (id) => {
    try {
      await getStopRequest({ id });
      fetchData();
    } catch (error) {
      console.error("Stop API Error:", error);
    }
  };
  const columns = [
    // {
    //   Header: (
    //     <MDBox display="flex" alignItems="center" justifyContent="flex-start">
    //       <Checkbox
    //         checked={isAllSelected}
    //         indeterminate={isIndeterminate}
    //         onChange={handleSelectAll}
    //         sx={{
    //           color: "#fff",
    //           border: "1px solid #000",
    //           borderRadius: "6px",
    //           width: "22px",
    //           height: "22px",
    //           "& .MuiSvgIcon-root": {
    //             fontSize: 22,
    //           },
    //           "&.Mui-checked": {
    //             color: "#fff",
    //             border: "1px solid #000",
    //           },
    //           "&.MuiCheckbox-indeterminate": {
    //             color: "#fff",
    //             border: "1px solid #000",
    //           },
    //           "&:hover": {
    //             border: "2px solid #000",
    //           },
    //         }}
    //       />
    //       <MDTypography
    //         variant="button"
    //         fontWeight="medium"
    //         color="text"
    //         sx={{ color: "#fff", userSelect: "none" }}
    //       >
    //         ALL
    //       </MDTypography>
    //     </MDBox>
    //   ),

    //   accessor: "select",
    //   align: "center",
    //   width: "8%",
    // },
    { Header: "ID", accessor: "id", align: "left" },
    { Header: "Number", accessor: "number", align: "left" },
    { Header: "Completed Calls", accessor: "completed_calls", align: "left" },
    { Header: "Current CC", accessor: "current_cc", align: "center" },
    { Header: "Count", accessor: "count", align: "center" },
    { Header: "Caller ID", accessor: "caller_id", align: "center" },
    { Header: "Trunk Name", accessor: "trunk_name", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    // { Header: "Audio Play", accessor: "audio_play", align: "left" },
    { Header: "action", accessor: "action", align: "center" },
  ];

  // Function to generate rows from campaign data
  const generateRows = (data) => {
    return data.map((item) => {
      const isChecked = selectedRows.some((id) => String(id) === String(item.id));

      return {
        select: (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ ml: -3 }}>
            <Checkbox
              checked={isChecked}
              onChange={() => handleSelectRow(item.id)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: "#1A73E8",
                border: "1px solid #000", // ✅ strong black border
                borderRadius: "6px",
                width: "22px",
                height: "22px",
                "& .MuiSvgIcon-root": {
                  fontSize: 22,
                },
                "&.Mui-checked": {
                  color: "#1A73E8",
                  border: "1px solid #000", // ✅ blue border on check
                },
                "&:hover": {
                  border: "1px solid #000", // ✅ blue border on hover
                },
              }}
            />
          </MDBox>
        ),

        id: (
          <MDTypography variant="button" fontWeight="medium">
            {item.id}
          </MDTypography>
        ),
        number: (
          <MDTypography variant="button" fontWeight="medium">
            {item.number}
          </MDTypography>
        ),

        completed_calls: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.completed_calls}
          </MDTypography>
        ),
        current_cc: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.current_cc || "-"}
          </MDTypography>
        ),

        count: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.count || "-"}
          </MDTypography>
        ),

        // caller_id: (
        //   <MDTypography variant="caption" fontWeight="medium">
        //     {item.caller_id}
        //   </MDTypography>
        // ),
        caller_id: (
          <Tooltip title={item.caller_id || "No Audio"}>
            <MDTypography variant="caption" fontWeight="medium">
              {item.caller_id
                ? item.caller_id.slice(0, 12) + (item.caller_id.length > 12 ? "..." : "")
                : ""}
            </MDTypography>
          </Tooltip>
        ),
        trunk_name: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.trunk_name || "-"}
          </MDTypography>
        ),
        status: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.status === 0
              ? "Not Started"
              : item.status === 1
              ? "Started"
              : item.status === 2
              ? "Pending"
              : item.status === 3
              ? "Complete"
              : "-"}
          </MDTypography>
        ),

        // audio_play: (
        //   <AudioToggle
        //     audioUrls={[item.audio_file_url, item.audio_file2_url, item.audio_file3_url]}
        //   />
        // ),

        action: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip title={"Start"}>
              <Icon
                color="success"
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => handleStart(item.id)}
              >
                <PlayArrowIcon />
              </Icon>
            </Tooltip>
            <Tooltip title={"Stop"}>
              <Icon
                color="error"
                sx={{ cursor: "pointer", fontSize: "14" }}
                onClick={() => handleStop(item.id)}
              >
                <PanToolIcon />
              </Icon>
            </Tooltip>
          </MDBox>
        ),
      };
    });
  };

  const fetchData = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }

    try {
      const res = await getIVRBlastListRequest(page + 1, perPage, searchTerm);

      const data = res?.data?.data?.data || [];
      const total = res?.data?.data?.total || 0;

      setCampaignData(data);
      setRows(generateRows(data));
      setTotalCount(total);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };
  useEffect(() => {
    fetchData(true); // Initial load with loader

    const interval = setInterval(() => {
      fetchData(false); // Refresh without loader
    }, 30000);

    return () => clearInterval(interval);
  }, [page, perPage, searchTerm]);

  useEffect(() => {
    setSelectedRows([]);
    fetchData();
  }, [page, perPage, searchTerm]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* <MDTypography variant="h6" color="white">
                {selectedRows.length > 0 && `(${selectedRows.length} selected)`}
              </MDTypography> */}
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDButton
                  variant="contained"
                  color="light"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchData()}
                  sx={{
                    color: "#1A73E8",
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  Refresh
                </MDButton>

                <MDTypography variant="h6" color="white">
                  {selectedRows.length > 0 && `(${selectedRows.length} selected)`}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDInput
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={{ backgroundColor: "#ffffff", borderRadius: "10%", minWidth: "200px" }}
                  variant="outlined"
                />
                <MDButton
                  onClick={() => {
                    setEditData(null);
                    setOpen(true);
                  }}
                  startIcon={<AddBoxIcon />}
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#1A73E8",
                    "&:hover": { backgroundColor: "#ffffff", color: "#1A73E8" },
                  }}
                >
                  Add IVR-Blast
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox pt={3}>
              {isLoading ? (
                <Grid container justifyContent="center" alignItems="center" sx={{ py: 5 }}>
                  <Loader />
                </Grid>
              ) : rows.length === 0 ? (
                <MDTypography sx={{ p: 3, textAlign: "center", color: "gray" }}>
                  No Record Found
                </MDTypography>
              ) : (
                <>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    loading={false}
                  />
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => {
                      setPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </>
              )}
            </MDBox>
          </Card>
        </Grid>
      </MDBox>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditData(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <IVRBlastForm
            onSuccess={() => {
              setOpen(false); // dialog close
              fetchData(); // table refresh
              setEditData(null);
            }}
            initialData={editData}
            clickedBtn={editData ? "edit" : "add"}
            isSubmitting={isSubmitting}
            setSubmitting={setIsSubmitting}
          />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default IVRBlastTable;
